import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';

const STATUS_COLORS = {
  pending: { bg:'#FEF6E0', color:'#E09A10', label:'Pending' },
  under_review: { bg:'#EBF5FF', color:'#1270B8', label:'Under Review' },
  approved: { bg:'#E6F7F1', color:'#0FA86A', label:'Approved' },
  declined: { bg:'#FDEAEA', color:'#D63A3A', label:'Declined' },
};

function fmt(n) {
  if (!n) return '—';
  return new Intl.NumberFormat('en-NG', { style:'currency', currency:'NGN' }).format(n);
}

export default function SADashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const isInitiator = user?.role === 'sa_initiator';
  const isApprover = user?.role === 'sa_approver';

  useEffect(() => {
    Promise.all([
      api.get('/api/sa/stats'),
      api.get('/api/sa'),
    ]).then(([s, r]) => {
      setStats(s.data);
      setRequests(r.data.requests);
    }).catch(console.error)
    .finally(() => setLoading(false));
  }, []);

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);

  if (loading) return <div className="loading">⏳ Loading…</div>;

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <div style={{ fontFamily:'Sora,sans-serif', fontSize:20, fontWeight:700, color:'var(--text)' }}>
            💰 Salary Advance / Overdraft
          </div>
          <div style={{ fontSize:12, color:'var(--text3)', marginTop:3 }}>
            {isInitiator ? 'Submit and track your salary advance requests' : isApprover ? 'Review and process advance requests' : 'Overview of all advance requests'}
          </div>
        </div>
        {isInitiator && (
          <button className="btn btn-primary" onClick={() => navigate('/sa/new')}>
            ➕ New Request
          </button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="stats-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
          {[
            { label:'Total Requests', val:stats.total, color:'var(--sky)', icon:'📋' },
            { label:'Pending', val:stats.pending, color:'#E09A10', icon:'⏳' },
            { label:'Under Review', val:stats.under_review, color:'#1270B8', icon:'🔍' },
            { label:'Approved', val:stats.approved, color:'#0FA86A', icon:'✅' },
          ].map(s => (
            <div key={s.label} className="card stat-card" style={{ padding:16, textAlign:'center' }}>
              <div style={{ fontSize:22, marginBottom:6 }}>{s.icon}</div>
              <div style={{ fontFamily:'Sora,sans-serif', fontSize:26, fontWeight:700, color:s.color }}>{s.val}</div>
              <div style={{ fontSize:11, color:'var(--text3)', marginTop:3 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filter chips */}
      <div className="chips" style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
        {['all','pending','under_review','approved','declined'].map(f => (
          <span key={f} className={`chip${filter===f?' active':''}`} onClick={() => setFilter(f)}
            style={{ fontSize:11, padding:'4px 12px', cursor:'pointer' }}>
            {f === 'all' ? 'All' : STATUS_COLORS[f]?.label || f}
            {f !== 'all' && stats && <span style={{ marginLeft:4, fontWeight:700 }}>({stats[f] || 0})</span>}
          </span>
        ))}
      </div>

      {/* Requests table */}
      <div className="table-wrap">
        <table className="data-table" style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ background:'var(--surface)', borderBottom:'2px solid var(--border)' }}>
              <th style={{ padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:600, color:'var(--text3)' }}>REQ NO.</th>
              <th style={{ padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:600, color:'var(--text3)' }}>ACCOUNT</th>
              <th style={{ padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:600, color:'var(--text3)' }}>BRANCH</th>
              <th style={{ padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:600, color:'var(--text3)' }}>REQUESTED</th>
              <th style={{ padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:600, color:'var(--text3)' }}>APPROVED</th>
              <th style={{ padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:600, color:'var(--text3)' }}>STATUS</th>
              <th style={{ padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:600, color:'var(--text3)' }}>DATE</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} style={{ padding:'30px', textAlign:'center', color:'var(--text3)', fontSize:13 }}>
                No requests found
              </td></tr>
            )}
            {filtered.map(r => {
              const s = STATUS_COLORS[r.status] || STATUS_COLORS.pending;
              return (
                <tr key={r.id} onClick={() => navigate(`/sa/${r.id}`)}
                  style={{ cursor:'pointer', borderBottom:'1px solid var(--border)' }}
                  onMouseOver={e => e.currentTarget.style.background='var(--sky-pale)'}
                  onMouseOut={e => e.currentTarget.style.background=''}>
                  <td style={{ padding:'10px 14px', fontFamily:'JetBrains Mono,monospace', fontSize:12, fontWeight:600, color:'var(--sky)' }}>{r.request_number}</td>
                  <td style={{ padding:'10px 14px' }}>
                    <div style={{ fontSize:12, fontWeight:500 }}>{r.account_name}</div>
                    <div style={{ fontSize:11, color:'var(--text3)' }}>{r.account_number}</div>
                  </td>
                  <td style={{ padding:'10px 14px', fontSize:12, color:'var(--text2)' }}>{r.branch}</td>
                  <td style={{ padding:'10px 14px', fontSize:12, fontWeight:600, color:'var(--text)' }}>{fmt(r.amount_requested)}</td>
                  <td style={{ padding:'10px 14px', fontSize:12, fontWeight:600, color:'#0FA86A' }}>{fmt(r.amount_approved)}</td>
                  <td style={{ padding:'10px 14px' }}>
                    <span style={{ background:s.bg, color:s.color, padding:'3px 10px', borderRadius:12, fontSize:11, fontWeight:600 }}>{s.label}</span>
                  </td>
                  <td style={{ padding:'10px 14px', fontSize:11, color:'var(--text3)' }}>{format(new Date(r.created_at),'dd MMM yyyy')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
